import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdGroups, MdDownloadDone, MdMenuBook, MdPayments, MdVerified } from 'react-icons/md';
import { formatINR, formatDateTime, statusBadgeClass } from '../utils/format';

const TABS = ['overview', 'quality', 'readers'];

export default function PartnerAnalytics() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [quality, setQuality] = useState([]);
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const fetchers = {
      overview: () => api.get('/admin/partner-analytics').then((r) => active && setOverview(r.data.data)),
      quality: () => api.get('/admin/partner-quality').then((r) => active && setQuality(r.data.data || [])),
      readers: () => api.get('/admin/partner-readers').then((r) => active && setReaders(r.data.data || [])),
    };
    fetchers[tab]().catch(() => toast.error('Failed to load')).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [tab]);

  return (
    <div className="page">
      <div className="page-header"><h2>Partner Analytics</h2></div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'overview' ? 'Overview' : t === 'quality' ? 'Quality Reports' : 'Active Readers'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : tab === 'overview' ? (
        <Overview d={overview} />
      ) : tab === 'quality' ? (
        <QualityTable rows={quality} />
      ) : (
        <ReadersTable rows={readers} />
      )}
    </div>
  );
}

function Overview({ d }) {
  if (!d) return <div className="empty-state"><h3>No data</h3></div>;
  const cards = [
    { label: 'Total Partners', value: d.total_partners, icon: MdGroups, color: '#C31721' },
    { label: 'Active Partners', value: d.active_partners, icon: MdVerified, color: '#10B981' },
    { label: 'Installs Today', value: d.installs_today, icon: MdDownloadDone, color: '#6366F1' },
    { label: 'Installs (30d)', value: d.installs_30d, icon: MdDownloadDone, color: '#F59E0B' },
    { label: 'Active Readers', value: d.active_readers, icon: MdMenuBook, color: '#2563EB' },
    { label: 'Total Paid', value: formatINR(d.total_paid), icon: MdPayments, color: '#16A34A' },
    { label: 'Pending Payouts', value: formatINR(d.pending_payouts), icon: MdPayments, color: '#D97706' },
  ];
  return (
    <>
      <div className="stats-grid">
        {cards.map((c, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ background: c.color + '15', color: c.color }}><c.icon /></div>
            <div className="stat-info"><h3>{c.value}</h3><p>{c.label}</p></div>
          </div>
        ))}
      </div>
      <div className="quick-links" style={{ marginTop: 24 }}>
        <h3>Top Partners by Quality</h3>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead><tr><th>Code</th><th>Name</th><th>Quality</th></tr></thead>
            <tbody>
              {(d.top_partners || []).map((p) => (
                <tr key={p.id}><td><strong>{p.partner_code}</strong></td><td>{p.name || '-'}</td><td>{p.quality_score}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function QualityTable({ rows }) {
  if (rows.length === 0) return <div className="empty-state"><h3>No partners</h3></div>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>Code</th><th>Name</th><th>Score</th><th>Band</th><th>Retention</th><th>Reading</th><th>Notif</th></tr></thead>
        <tbody>
          {rows.map((p) => {
            const q = p.quality || {};
            return (
              <tr key={p.id}>
                <td><strong>{p.partner_code}</strong></td>
                <td>{p.name || '-'}</td>
                <td>{p.quality_score}</td>
                <td><span className={`badge ${statusBadgeClass(p.quality_band)}`}>{p.quality_band || '-'}</span></td>
                <td>{q.retention_component != null ? `${Number(q.retention_component).toFixed(0)}` : '-'}</td>
                <td>{q.reading_component != null ? `${Number(q.reading_component).toFixed(0)}` : '-'}</td>
                <td>{q.notification_component != null ? `${Number(q.notification_component).toFixed(0)}` : '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReadersTable({ rows }) {
  if (rows.length === 0) return <div className="empty-state"><h3>No active readers</h3></div>;
  const mask = (dv) => (dv && dv.length > 8 ? `${dv.slice(0, 4)}…${dv.slice(-4)}` : dv || '-');
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>Device</th><th>Partner</th><th>Articles</th><th>Active days</th><th>Last active</th><th>Retained</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{mask(r.device_id)}</td>
              <td>{r.partner_id || '-'}</td>
              <td>{r.articles_read}</td>
              <td>{r.active_days}</td>
              <td>{formatDateTime(r.last_active_at)}</td>
              <td>
                {r.day30_active ? <span className="badge badge-success">30d</span> : r.day7_active ? <span className="badge badge-info">7d</span> : <span className="badge badge-neutral">new</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
